import React from 'react';
import { TransferComparisonData } from '../../api/pension-plan-transfers';
import { ArcaPlan } from '../../constants/arcaPlans';
import { PlanReturnView, TransferReturns } from '../../utils/transferReturns';
import ReportSettingsCard, { ReportSettings } from './ReportSettingsCard';
import TransferProjectionCard from './TransferProjectionCard';
import RetrospectiveCard from './RetrospectiveCard';

interface ReportSimulatorProps {
	comparisonData: TransferComparisonData;
	/** Plan Arca destino (lado derecho de la comparación, derivado localmente). */
	resolvedPlan: ArcaPlan;
	/** Rentabilidades decididas por comparsa (`/plan-returns`): gráfica, tabla y retrospectiva. */
	returns: TransferReturns & { arca: PlanReturnView };
	settings: ReportSettings;
	onChange: (patch: Partial<ReportSettings>) => void;
	/** Aportación mensual máxima deducible (€). */
	maxMonthly: number;
	translate: (key: string) => string;
}

/**
 * Sección interactiva "Ajusta tu informe": card de ajustes (izquierda ~40%) y, a la
 * derecha (~60%), la gráfica de futuro con la retrospectiva del periodo comparado debajo (pasado tras
 * el futuro). La columna derecha estira la gráfica para igualar la altura de los ajustes.
 * Todo proyecta el importe traspasado (saldo × %) y reacciona al vuelo.
 */
const ReportSimulator: React.FC<ReportSimulatorProps> = ({
	comparisonData,
	resolvedPlan,
	returns,
	settings,
	onChange,
	maxMonthly,
	translate
}) => {
	const { originPlan } = comparisonData;
	const { arca, origin, samePeriod } = returns;
	const transferredAmount = (settings.balance * settings.transferPct) / 100;

	return (
		<div className="grid grid-cols-1 gap-4 lg:grid-cols-5 lg:items-stretch">
			<div className="lg:col-span-2">
				<ReportSettingsCard
					settings={settings}
					onChange={onChange}
					maxMonthly={maxMonthly}
					translate={translate}
				/>
			</div>
			<div className="flex flex-col gap-4 lg:col-span-3">
				<div className="flex-1">
					<TransferProjectionCard
						balance={transferredAmount}
						years={settings.years}
						monthlyContribution={settings.monthlyContribution}
						arca={arca}
						origin={origin}
						samePeriod={samePeriod}
						arcaPlanName={resolvedPlan.name || resolvedPlan.manager || 'Arca Digital'}
						arcaShortName={resolvedPlan.manager}
						currentPlanName={originPlan.name || originPlan.manager || ''}
						translate={translate}
					/>
				</div>
				{/* «Los últimos N años»: solo con datos de los dos planes y el mismo periodo. */}
				{origin && samePeriod && (
					<RetrospectiveCard
						transferredAmount={transferredAmount}
						arcaReturnPct={arca.annualPct}
						originReturnPct={origin.annualPct}
						returnPeriodYears={arca.periodYears}
						translate={translate}
					/>
				)}
			</div>
		</div>
	);
};

export default ReportSimulator;
